/**
 * Production board feet — linked to materials timber list totals by default,
 * with optional per-structure override that persists across edits.
 */

import type { MaterialsNodeData, ProductionNodeData, ProposalNodeSet } from '../types';

export function sumTimberListBoardFeet(
    timberList: { boardFeet: number; quantity: number }[],
): number {
    return timberList.reduce((sum, item) => sum + (item.boardFeet || 0) * (item.quantity || 0), 0);
}

export function getMaterialsTotalBoardFeet(materials: Pick<MaterialsNodeData, 'timberList'>): number {
    return sumTimberListBoardFeet(materials.timberList || []);
}

/** Shop board feet is shown and costed as a whole number. */
export function roundProductionBoardFeet(boardFeet: number): number {
    const n = Number(boardFeet);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n);
}

/** True only when the user explicitly turned override on. Off by default. */
export function isProductionBoardFeetOverridden(
    production: ProductionNodeData,
    _materialsBoardFeet?: number,
): boolean {
    return production.boardFeetOverridden === true;
}

export function resolveProductionBoardFeet(
    production: ProductionNodeData,
    materialsBoardFeet: number,
): number {
    return isProductionBoardFeetOverridden(production, materialsBoardFeet)
        ? production.boardFeet
        : roundProductionBoardFeet(materialsBoardFeet);
}

/** Apply materials total to production when not overridden; returns a new object. */
export function syncProductionBoardFeetFromMaterials(
    production: ProductionNodeData,
    materialsBoardFeet: number,
): ProductionNodeData {
    if (isProductionBoardFeetOverridden(production, materialsBoardFeet)) {
        return production;
    }
    return {
        ...production,
        boardFeet: roundProductionBoardFeet(materialsBoardFeet),
        boardFeetOverridden: false,
    };
}

export function setProductionBoardFeetOverride(
    production: ProductionNodeData,
    boardFeet: number,
    materialsBoardFeet: number,
): ProductionNodeData {
    const overridden = Math.abs(boardFeet - roundProductionBoardFeet(materialsBoardFeet)) > 0.001;
    return {
        ...production,
        boardFeet,
        boardFeetOverridden: overridden,
    };
}

export function clearProductionBoardFeetOverride(
    production: ProductionNodeData,
    materialsBoardFeet: number,
): ProductionNodeData {
    return {
        ...production,
        boardFeet: roundProductionBoardFeet(materialsBoardFeet),
        boardFeetOverridden: false,
    };
}

/** Unlock manual board feet editing while keeping the current displayed value. */
export function enableProductionBoardFeetOverride(
    production: ProductionNodeData,
    materialsBoardFeet: number,
): ProductionNodeData {
    return {
        ...production,
        boardFeet: resolveProductionBoardFeet(production, materialsBoardFeet),
        boardFeetOverridden: true,
    };
}

export function normalizeProductionBoardFeetOnLoad(nodeSet: ProposalNodeSet): ProposalNodeSet {
    const materialsBf = getMaterialsTotalBoardFeet(nodeSet.materials);
    const overridden = isProductionBoardFeetOverridden(nodeSet.production, materialsBf);
    return {
        ...nodeSet,
        production: {
            ...nodeSet.production,
            boardFeet: overridden ? nodeSet.production.boardFeet : roundProductionBoardFeet(materialsBf),
            boardFeetOverridden: overridden,
        },
    };
}
